import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './Statistic.css';

import { getTechnics, getOrders } from './statisticApi';
import { selectLanguage, selectRole, selectUserId } from '../Login/loginSlice';
import {
  setAudios,
  setColor,
  setComments,
  setOrder,
  setPacient,
  setPaid,
  setPhotos,
  setPrice,
  setShowOrderCard,
  setTechnicId,
} from '../Lab/Orders/orderSlice';

import { setShowDoctorOrders } from '../Lab/labSlice';
import { useNavigate } from 'react-router-dom';
import { texts } from '../Common/common';
import { getOrderAudios, getOrderComments, getOrderPhotos } from '../Lab/Orders/orderApi';
import { DoctorsTab, PaymentTab, RevenueTab, TechniciansTab } from './StatisticCharts';
import { startOfMonthYMD, toLocalYMD } from './chartUtils';

const TABS = [
  { id: 'report', labelKey: 'report' },
  { id: 'revenue', labelKey: 'chartRevenue' },
  { id: 'technicians', labelKey: 'chartTechnicians' },
  { id: 'doctors', labelKey: 'chartDoctors' },
  { id: 'payment', labelKey: 'chartPayment' },
];

export const Statistic = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const role = useSelector(selectRole);
  const user_id = useSelector(selectUserId);
  const currentLanguage = useSelector(selectLanguage);
  const t = texts[currentLanguage];

  const [activeTab, setActiveTab] = useState('report');
  const [selectedTechnicIndex, setSelectedTechnicIndex] = useState(0);
  const [technics, setTechnics] = useState([]);
  const [technicsFilter, setTechnicsFilter] = useState([]);
  const [orders, setOrders] = useState([]);

  const [dateStart, setDateStart] = useState(() => startOfMonthYMD());
  const [dateEnd, setDateEnd] = useState(() => toLocalYMD(new Date()));

  function handlePressOrder(order) {
    dispatch(setOrder(order));
    dispatch(setPacient(order.pacient));
    dispatch(setPaid(order.paid));
    dispatch(setTechnicId(order.technic_id));
    dispatch(setPrice(order.price));
    dispatch(setColor(order.color));

    getOrderPhotos({ order_id: order.id }, (data) => {
      dispatch(setPhotos(data.photos || []));
    });

    getOrderAudios({ order_id: order.id }, (data) => {
      dispatch(setAudios(data.audios || []));
    });

    getOrderComments({ order_id: order.id }, (data) => {
      dispatch(setComments(data.comments || []));
    });

    dispatch(setShowDoctorOrders(true));
    dispatch(setShowOrderCard(true));
  }

  const fetchOrders = (opts = {}) => {
    const params = {
      user_id: user_id,
      date_start: opts.date_start ?? dateStart + ' 00:00:00',
      date_end: opts.date_end ?? dateEnd + ' 23:59:59',
    };
    getOrders(params, (data) => {
      setOrders(data.orders || []);
    });
  };

  useEffect(() => {
    if (role === 'doctor') return;
    fetchOrders({ date_start: dateStart + ' 00:00:00', date_end: dateEnd + ' 23:59:59' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (role === 'doctor') return;
    getTechnics({ user_id }, (data) => {
      const technicsObj = data.technics || [];
      setTechnics(technicsObj);
      const filter = [t.all, ...Object.keys(technicsObj).map((i) => `${technicsObj[i].name} ${technicsObj[i].surname}`)];
      setTechnicsFilter(filter);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalSelectedTechnicPrice = useMemo(() => {
    const selectedTechnic = technics[selectedTechnicIndex - 1];
    return orders
      .filter((order) => (selectedTechnic ? order.technic_id === selectedTechnic.id : true) || selectedTechnicIndex === 0)
      .reduce((acc, order) => acc + (order.price * ((order.commission || 0) / 100) || 0), 0);
  }, [orders, technics, selectedTechnicIndex]);

  const commission = totalSelectedTechnicPrice;

  const applyDates = (start, end) => {
    setDateStart(start);
    setDateEnd(end);
    fetchOrders({ date_start: start + ' 00:00:00', date_end: end + ' 23:59:59' });
  };

  const onChangeDateStart = (e) => {
    applyDates(e.target.value, dateEnd);
  };

  const onChangeDateEnd = (e) => {
    applyDates(dateStart, e.target.value);
  };

  const applyPreset = (kind) => {
    const end = new Date();
    const start = new Date();
    if (kind === 'week') start.setDate(end.getDate() - 6);
    if (kind === 'month') start.setDate(1);
    if (kind === 'quarter') {
      start.setMonth(end.getMonth() - 2);
      start.setDate(1);
    }
    if (kind === 'year') {
      start.setMonth(0);
      start.setDate(1);
    }
    applyDates(toLocalYMD(start), toLocalYMD(end));
  };

  return (
    <div className="container" style={role === 'doctor' ? { display: 'none' } : undefined}>
      <div className="header">
        <h2 className="headerTitle">{t.statistics}</h2>
      </div>

      <div className="statTabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`statTab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {t[tab.labelKey]}
          </button>
        ))}
      </div>

      <div className="filterBox">
        <div className="filterLabel">{t.filters}</div>

        {activeTab === 'report' && (
          <div className="filterRow">
            <label className="label">{t.technician}</label>
            <div className="inputWrapper">
              <select
                className="select"
                value={selectedTechnicIndex}
                onChange={(e) => setSelectedTechnicIndex(Number(e.target.value))}
              >
                {technicsFilter.map((technicName, idx) => (
                  <option key={idx} value={idx}>
                    {technicName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="filterRow">
          <label className="label">{t.period}</label>
          <div className="dateGroup">
            <input type="date" className="dateInput" value={dateStart} onChange={onChangeDateStart} />
            <div className="divider" />
            <input type="date" className="dateInput" value={dateEnd} onChange={onChangeDateEnd} />
          </div>
        </div>

        <div className="presetRow">
          {['week', 'month', 'quarter', 'year'].map((kind) => (
            <button key={kind} type="button" className="presetBtn" onClick={() => applyPreset(kind)}>
              {t[kind]}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'report' && (
        <>
          <div className="headBlock">
            <div className="headLabel">{t.salary}</div>
            <div className="salaryRow">
              <div className="salaryText">
                {technics[selectedTechnicIndex - 1]?.commission}% - {commission} {t.rub}
              </div>
            </div>
          </div>

          <div className="listContainer">
            <div className="scrollList">
              {orders.map((order, index) => {
                const visible =
                  selectedTechnicIndex === 0 || order.technic_id === technics[selectedTechnicIndex - 1]?.id;
                if (!visible) return null;
                return (
                  <div
                    key={index}
                    className="orderItem"
                    onClick={() => {
                      handlePressOrder(order);
                      navigate('/lab', { state: { orderId: order.id } });
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={() => {}}
                  >
                    <div className="rowBetween">
                      <span>№{order.number || order.id}</span>
                      <span className="grayText">{(order.date || '').slice(0, 10)}</span>
                    </div>

                    <div className="rowBetween">
                      <span>
                        {t.patientLabel}: {order.patient_name}
                      </span>
                      <span className="grayText">
                        {t.doctor}: {order.doctor_name}
                      </span>
                    </div>

                    <div className="rowBetween">
                      <span>
                        {t.priceLabel}: {order.price}
                      </span>
                      <span className="grayText">
                        {t.paid}: {order.paid}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {activeTab === 'revenue' && <RevenueTab orders={orders} t={t} />}
      {activeTab === 'technicians' && <TechniciansTab orders={orders} technics={technics} t={t} />}
      {activeTab === 'doctors' && <DoctorsTab orders={orders} t={t} />}
      {activeTab === 'payment' && <PaymentTab orders={orders} t={t} />}
    </div>
  );
};

export default Statistic;
